import React, { useEffect, useMemo, useState } from 'react';
import { Collapse, Empty, Input, Spin } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../store/store';
import { getShapeBank } from '../../requests/documentDatas';
import { getPrimaryDark } from '../../constants/constants';
import DuotoneLightIcon from '../common/DuotoneLightIcon';

// Cache module (clé = type:id du document) : même principe que le cache de la
// banque de médias (cf. MediaBankPicker) — la banque ne change pas pendant
// l'édition, sauf après une sauvegarde (shapeBankVersion, cf. Shape.jsx).
const bankCache = new Map();

// data-outside-ignore + garde mousedown/click : même remède que MediaBankPicker
// (le clic sur l'entête d'un dossier Collapse bubble vers l'ancêtre qui
// désélectionne la box).
const ShapeBankThumbnail = ({ shape, onSelectShape }) => (
  <button
    type="button"
    data-outside-ignore="true"
    onMouseDown={(e) => e.preventDefault()}
    onClick={(e) => {
      e.preventDefault();
      e.stopPropagation();
      onSelectShape(shape);
    }}
    title={shape.name}
    style={thumbButtonStyle}
  >
    {shape.thumbnail_url ? (
      <img src={shape.thumbnail_url} alt={shape.name} loading="lazy" style={thumbImgStyle} />
    ) : (
      <DuotoneLightIcon name='vector-polygon' style={{ fontSize: 22, opacity: 0.35 }} />
    )}
  </button>
);

// Sélecteur de forme composée à insérer, posé à la place de la sélection
// courante — même principe que MediaBankPicker pour une image, en plus simple
// (pas de paliers de résolution ni de filtrage par famille de média).
const ShapeBankPicker = ({ onSelectShape }) => {
  const { t } = useTranslation();
  const currentDocument = useStore(({ currentDocument }) => currentDocument);
  const shapeBankVersion = useStore(({ shapeBankVersion }) => shapeBankVersion);
  const accentColor = getPrimaryDark(currentDocument?.type === 'template');
  const cacheKey = `${currentDocument?.type}:${currentDocument?.id}:${shapeBankVersion}`;

  const [folders, setFolders] = useState(() => bankCache.get(cacheKey) ?? null);
  const [loading, setLoading] = useState(!bankCache.has(cacheKey));
  const [query, setQuery] = useState('');
  const [openFolders, setOpenFolders] = useState([]);

  useEffect(() => {
    if (bankCache.has(cacheKey)) {
      setFolders(bankCache.get(cacheKey));
      setLoading(false);
      return;
    }
    if (!currentDocument?.id) {
      setFolders([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getShapeBank(currentDocument).then((data) => {
      if (cancelled) return;
      const list = (data ?? []).filter((folder) => folder.shapes?.length > 0);
      bankCache.set(cacheKey, list);
      setFolders(list);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [cacheKey, currentDocument]);

  const visible = useMemo(() => {
    if (!query) return folders ?? [];
    const q = query.toLowerCase();
    return (folders ?? [])
      .map((folder) => ({ ...folder, shapes: folder.shapes.filter((s) => s.name?.toLowerCase().includes(q)) }))
      .filter((folder) => folder.shapes.length > 0);
  }, [folders, query]);

  const matchedKeys = visible.map((folder) => String(folder.id)).join(',');
  useEffect(() => {
    setOpenFolders(query && matchedKeys ? matchedKeys.split(',') : []);
  }, [query, matchedKeys]);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 12 }}>
        <Spin size="small" />
      </div>
    );
  }

  if ((folders ?? []).length === 0) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('no-bank-shapes')} />;
  }

  const items = visible.map((folder) => ({
    key: String(folder.id),
    label: (
      <span style={{ fontSize: 12, fontWeight: 600, color: accentColor }}>
        {folder.name}
        <span style={{ color: 'rgba(11,34,57,0.45)', marginLeft: 6, fontWeight: 400 }}>
          ({folder.shapes.length})
        </span>
      </span>
    ),
    children: (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
        {folder.shapes.map((shape) => (
          <ShapeBankThumbnail key={shape.id} shape={shape} onSelectShape={onSelectShape} />
        ))}
      </div>
    ),
  }));

  return (
    <div
      data-outside-ignore="true"
      onMouseDown={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <Input
        size="small"
        allowClear
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onMouseDown={(e) => e.stopPropagation()}
        placeholder={t('search-shape')}
        prefix={<FontAwesomeIcon icon={icon({ name: 'magnifying-glass', style: 'solid' })} style={{ opacity: 0.45 }} />}
        style={{ marginBottom: 8 }}
      />
      {items.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('no-result')} />
      ) : (
        <Collapse ghost size="small" items={items} activeKey={openFolders} onChange={setOpenFolders} />
      )}
    </div>
  );
};

const thumbButtonStyle = {
  width: '100%',
  padding: 0,
  border: '1px solid rgba(11,34,57,0.12)',
  borderRadius: 6,
  background: '#fafafa',
  cursor: 'pointer',
  height: 56,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
};

const thumbImgStyle = { maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' };

export default ShapeBankPicker;
